import { Box, Typography } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { ChevronLeft } from 'src/components/icons/chevron_left';
import { ChevronRight } from 'src/components/icons/chevron_right';
import {
  PaginationAction,
  usePagination,
} from 'src/components/pagination/use_pagination';

/**
 * Pagination's view model
 *
 * @param {number} pageSize: The size of each page
 * @param {number} currentPage: The current selected page
 * @param {number} itemCount: The total number of items
 */
export type PaginationViewModel = {
  pageSize: number;
  currentPage: number;
  itemCount: number;
};

type PaginationProps = {
  data: PaginationViewModel;
  onPageSelected: (pageIndex: number) => void;
};

export const Pagination: React.FC<PaginationProps> = ({
  data,
  onPageSelected,
}) => {
  const [pagination, updatePagination, canNavigate] = usePagination({
    itemCount: data.itemCount,
    pageSize: data.pageSize,
    currentPage: data.currentPage,
  });

  const handlePageChange = (action: PaginationAction, pageNum?: number) => {
    const newPage = updatePagination(action, pageNum);
    onPageSelected(newPage);
    return newPage;
  };

  if (pagination.startPage > pagination.endPage || pagination.totalPages <= 0) {
    return <Box>Loading pagination...</Box>;
  }

  if (data.itemCount <= data.pageSize) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
        }}
      >
        Showing all {data.itemCount} items
      </Typography>
    );
  }

  return (
    <PaginationWrapper>
      <PaginationContainer>
        <TextControlButton
          disabled={!canNavigate(PaginationAction.SelectFirstPage)}
          onClick={() => handlePageChange(PaginationAction.SelectFirstPage)}
          aria-label='First Page'
        >
          First
        </TextControlButton>

        <StyledChevronLeftIcon
          disabled={!canNavigate(PaginationAction.SelectPrevPage)}
          onClick={() => handlePageChange(PaginationAction.SelectPrevPage)}
          aria-label='Previous Page'
        />

        {Array.from(
          { length: pagination.endPage - pagination.startPage + 1 },
          (_, index) => {
            const pageNum = index + pagination.startPage;
            return (
              <NumberControlButton
                onClick={() =>
                  handlePageChange(PaginationAction.SelectPage, pageNum)
                }
                key={pageNum}
                selected={pageNum === pagination.currentPage}
              >
                {pageNum}
              </NumberControlButton>
            );
          },
        )}

        <StyledChevronRightIcon
          disabled={!canNavigate(PaginationAction.SelectNextPage)}
          aria-label='Next Page'
          onClick={() => handlePageChange(PaginationAction.SelectNextPage)}
        />

        <TextControlButton
          disabled={!canNavigate(PaginationAction.SelectLastPage)}
          onClick={() => handlePageChange(PaginationAction.SelectLastPage)}
          aria-label='Last Page'
        >
          Last
        </TextControlButton>
      </PaginationContainer>

      <TotalPagesText>{pagination.totalPages} Pages</TotalPagesText>
    </PaginationWrapper>
  );
};

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const TotalPagesText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightRegular,
}));

const NumberControlButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ selected, theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '20px',
  height: '20px',
  padding: '10px',
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  border: 'none',
  borderRadius: '5px',
  '&:hover': {
    color: selected
      ? theme.palette.primary.contrastText
      : theme.palette.primary.main,
    backgroundColor: selected
      ? theme.palette.primary.main
      : theme.palette.action.hover,
    cursor: 'pointer',
  },
}));

const TextControlButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: boolean }>(({ disabled, theme }) => ({
  padding: '5px',
  height: '20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.text.primary,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  borderRadius: '5px',
  '&:hover': {
    color: disabled ? theme.palette.text.secondary : theme.palette.primary.main,
    backgroundColor: disabled ? 'transparent' : theme.palette.action.hover,
    cursor: disabled ? 'crosshair' : 'pointer',
  },
  opacity: disabled ? 0.5 : 1,
  pointerEvents: disabled ? 'none' : 'auto',
}));

// Shared styles for icon buttons to avoid duplication
const getIconButtonStyles = ({
  disabled,
  theme,
}: {
  disabled?: boolean;
  theme: Theme;
}) => ({
  height: '20px',
  width: '20px',
  padding: '5px',
  display: 'flex',
  borderRadius: '5px',
  '&:hover': {
    color: disabled ? 'inherit' : theme.palette.primary.main,
    backgroundColor: disabled ? 'transparent' : theme.palette.action.hover,
    cursor: disabled ? 'default' : 'pointer',
  },
  opacity: disabled ? 0.25 : 1,
  pointerEvents: (disabled ? 'none' : 'auto') as 'none' | 'auto',
});

// Styled icon components using shared styles - no code duplication!
const StyledChevronRightIcon = styled(ChevronRight, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: boolean }>(getIconButtonStyles);

const StyledChevronLeftIcon = styled(ChevronLeft, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: boolean }>(getIconButtonStyles);
